/**
 * src/hooks/usePushNotifications.js
 * Manages service worker registration and push subscription lifecycle.
 */

import { useState, useEffect, useCallback } from 'react';
import { subscribeToAdminPush, unsubscribeFromAdminPush, getPushStatus, getVapidPublicKey } from '../utils/api';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64   = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw      = window.atob(base64);
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
}

export function usePushNotifications() {
  const [supported,   setSupported]   = useState(false);
  const [permission,  setPermission]  = useState('default');
  const [subscribed,  setSubscribed]  = useState(false);
  const [loading,     setLoading]     = useState(false);
  const [swReg,       setSwReg]       = useState(null);

  // Register service worker and check current state
  useEffect(() => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setSupported(false);
      return;
    }
    setSupported(true);
    setPermission(Notification.permission);

    navigator.serviceWorker.register('/service-worker.js')
      .then(reg => {
        setSwReg(reg);

        // Listen for RESUBSCRIBE messages from SW
        navigator.serviceWorker.addEventListener('message', async (e) => {
          if (e.data?.type === 'RESUBSCRIBE' && e.data.subscription) {
            try { await subscribeToAdminPush(e.data.subscription); } catch {}
          }
        });

        return reg;
      })
      .catch(err => console.error('[push] SW registration failed:', err));

    // Check server-side subscription status
    getPushStatus()
      .then(data => setSubscribed(data.subscribed || false))
      .catch(() => {});
  }, []);

  const subscribe = useCallback(async () => {
    if (!swReg || !supported) return false;
    setLoading(true);

    try {
      // Request permission
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') {
        setLoading(false);
        return false;
      }

      // Get VAPID public key
      const { publicKey } = await getVapidPublicKey();
      const applicationServerKey = urlBase64ToUint8Array(publicKey);

      // Subscribe
      const sub = await swReg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });

      // Save to server
      await subscribeToAdminPush(sub.toJSON());
      setSubscribed(true);
      return true;
    } catch (err) {
      console.error('[push] subscribe failed:', err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [swReg, supported]);

  const unsubscribe = useCallback(async () => {
    if (!swReg) return;
    setLoading(true);

    try {
      const sub = await swReg.pushManager.getSubscription();
      if (sub) {
        await unsubscribeFromAdminPush(sub.endpoint);
        await sub.unsubscribe();
      }
      setSubscribed(false);
    } catch (err) {
      console.error('[push] unsubscribe failed:', err);
    } finally {
      setLoading(false);
    }
  }, [swReg]);

  return { supported, permission, subscribed, loading, subscribe, unsubscribe };
}
